import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../constants";
import {
  endpointInstance,
  formatTicketDate,
  onlyDigits,
  validatePayment,
} from "../methods/Methods";
import { PaymentData, RequestRecharge, TopUpPackage } from "../types/common";

interface PayWithCardProps {
  phone: string;
  selectedPackage: TopUpPackage;
  payment: PaymentData;
  onPaymentChange: (field: keyof PaymentData, value: string) => void;
  onPaymentCompleted: (topUpNumber: string, topUpDate: string) => void;
}

const bytesToBinaryString = (bytes: Uint8Array): string => {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return binary;
};

const encryptText = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  return window.btoa(bytesToBinaryString(bytes));
};

function PayWithCard({
  phone,
  selectedPackage,
  payment,
  onPaymentChange,
  onPaymentCompleted,
}: PayWithCardProps) {
  const navigate = useNavigate();
  const [paymentError, setPaymentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const call = endpointInstance;

  const onPaymentInputChange = (field: keyof PaymentData, value: string) => {
    if (paymentError) {
      setPaymentError("");
    }
    onPaymentChange(field, value);
  };

  const onSubmitAndNavigate = async () => {
    setPaymentError("");

    const currentError = validatePayment(payment);
    if (currentError) {
      setPaymentError(currentError);
      return false;
    }

    setIsSubmitting(true);
    try {
      const rechargesPayload = call.getLastRecharges();
      if (!rechargesPayload) {
        setPaymentError(
          "No fue posible obtener los datos de la línea. Valida tu número nuevamente.",
        );
        return false;
      }

      const normalizedCustomerId = onlyDigits(
        `${rechargesPayload?.contrato ?? ""}`,
      );
      if (normalizedCustomerId.length < 10) {
        setPaymentError(
          "No fue posible obtener los datos de la línea para procesar el pago.",
        );
        return false;
      }

      const normalizedRechargeNumber = onlyDigits(
        `${rechargesPayload?.numero ?? phone}`,
      );
      if (normalizedRechargeNumber.length !== 10) {
        setPaymentError("No fue posible obtener el número de recarga.");
        return false;
      }

      const expiryDigits = onlyDigits(payment.expiry);
      const body: RequestRecharge = {
        sucursal: encryptText(normalizedCustomerId.substring(0, 3)),
        suscriptor: encryptText(normalizedCustomerId.substring(3, 10)),
        numero: encryptText(normalizedRechargeNumber),
        id_paquete: encryptText(selectedPackage.id),
        name: encryptText(payment.cardholder.trim().toUpperCase()),
        tarjeta: encryptText(onlyDigits(payment.cardnumber)),
        expmonth: encryptText(expiryDigits.substring(0, 2)),
        expyear: encryptText(expiryDigits.substring(2, 4)),
        cvv_csc: encryptText(payment.cvv),
      };

      const response = await call.payRechargeWithCard(body);
      const rawPayload = response.data?.data ?? response.data;
      const payload = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
      const status = `${payload?.estatus ?? ""}`.trim().toLowerCase();
      const message = `${payload?.mensaje ?? ""}`.trim();
      const isSuccess =
        status === "exito" ||
        status === "éxito" ||
        (!status && /exit|aprobad|correct/i.test(message));

      // if (isSuccess) {
      if (!isSuccess) {
        setPaymentError(
          message || "No fue posible procesar el pago. Intenta nuevamente.",
        );
        return false;
      }

      const paidAmount = Number(selectedPackage.price);
      const paidAmountLabel = Number.isFinite(paidAmount)
        ? `$${Number.isInteger(paidAmount) ? paidAmount : paidAmount.toFixed(2)}`
        : `${selectedPackage.price}`;

      setPaymentError("");
      onPaymentCompleted(paidAmountLabel, formatTicketDate(new Date()));
      navigate(ROUTES.PAYMENTS);
      return true;
    } catch (error: any) {
      const rawErrorPayload =
        error?.response?.data?.data ?? error?.response?.data;
      const errorPayload = Array.isArray(rawErrorPayload)
        ? rawErrorPayload[0]
        : rawErrorPayload;
      const apiMessage = `${errorPayload?.mensaje ?? ""}`.trim();

      setPaymentError(
        apiMessage || "No fue posible procesar el pago. Intenta nuevamente.",
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmitAndNavigate();
  };

  return (
    <>
      <h2 className="step-title">
        <span>3</span> Ingresa tus datos de pago
      </h2>
      <h3 className="step-title">{phone}</h3>

      <form
        className="form-payment d-flex flex-column mt-5"
        onSubmit={onSubmit}
      >
        <input
          type="text"
          name="cardholder"
          placeholder="Nombre del titular*"
          value={payment.cardholder}
          onChange={(event) =>
            onPaymentInputChange("cardholder", event.target.value)
          }
        />

        <input
          type="text"
          name="cardnumber"
          inputMode="numeric"
          placeholder="Número de tarjeta*"
          value={payment.cardnumber}
          onChange={(event) =>
            onPaymentInputChange("cardnumber", event.target.value)
          }
        />

        <div className="row">
          <div className="col-6">
            <input
              type="text"
              name="expiry"
              inputMode="numeric"
              placeholder="Fecha de expiración*"
              value={payment.expiry}
              onChange={(event) =>
                onPaymentInputChange("expiry", event.target.value)
              }
            />
          </div>
          <div className="col-6">
            <input
              type="text"
              name="cvv"
              inputMode="numeric"
              placeholder="CVV*"
              value={payment.cvv}
              onChange={(event) =>
                onPaymentInputChange("cvv", event.target.value)
              }
            />
          </div>
        </div>

        {paymentError ? (
          <p
            className="alert alert-danger alert-compact payment-error-message mb-0 text-center"
            role="alert"
          >
            {paymentError}
          </p>
        ) : null}

        <div className="payment-actions">
          <label className="terms-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            Acepto los{" "}
            <a href="https://www.megacable.com.mx/terminos-y-condiciones" target="_blank" rel="noopener noreferrer">
              Términos y Condiciones
            </a>
          </label>
          <button
            type="submit"
            className="payment-submit-btn"
            disabled={isSubmitting || !termsAccepted}
          >
            {isSubmitting ? "Procesando pago..." : "Continuar"}
          </button>
          <button
            type="button"
            className="payment-cancel-btn"
            disabled={isSubmitting}
            onClick={() => navigate(ROUTES.LANDING)}
          >
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}

export default PayWithCard;
